import { useEffect, useState } from "react";
import { ApiError } from "../../../api/http";
import { fetchStaffWork, type WorkTask } from "../../../api/staffApi";
import { EmptyState } from "../components/EmptyState";
import { SkeletonCard } from "../components/SkeletonCard";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";

function TaskBlock({ label, task }: { label: string; task: WorkTask | null }) {
  if (!task) return null;
  return (
    <article className="staff-work-card">
      <p className="staff-page-eyebrow">{label}</p>
      <h3>{task.title}</h3>
      {task.detail ? <p className="muted">{task.detail}</p> : null}
      <p className="staff-work-card__meta">
        <span className="staff-badge staff-badge--accent">{task.status}</span>
        {task.scheduledTime ? <span className="muted">{task.scheduledTime}</span> : null}
      </p>
    </article>
  );
}

export function StaffWorkPage() {
  const [currentTask, setCurrentTask] = useState<WorkTask | null>(null);
  const [nextTask, setNextTask] = useState<WorkTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const work = await fetchStaffWork();
        setCurrentTask(work.currentTask);
        setNextTask(work.nextTask);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Could not load work.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <StaffAppShell title="Work">
      <div className="staff-page">
        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}
        {loading ? (
          <SkeletonCard />
        ) : !currentTask && !nextTask ? (
          <EmptyState title="No tasks assigned" detail="Event work will appear here when you're assigned." />
        ) : (
          <div className="staff-card-stack">
            <TaskBlock label="Now" task={currentTask} />
            <TaskBlock label="Up next" task={nextTask} />
          </div>
        )}
      </div>
    </StaffAppShell>
  );
}
